import React from 'react'
import type { Plan } from '@/modules/plans/types'
import { getPlanDiscountPercent } from '@/modules/plans/utils/pricing'

type Coupon = {
  ok?: boolean
  type?: 'percent' | 'fixed'
  percent_off?: number
  amount_off_cents?: number
}

type Props = {
  plan: Plan | null
  coupon: Coupon | null
  couponCode?: string
  affiliateLabel?: string | null
  billingInterval?: 'month' | 'year'
  isTrialMode?: boolean
}

export default function CheckoutSummaryCard({
  plan,
  coupon,
  couponCode,
  affiliateLabel,
  billingInterval,
  isTrialMode,
}: Props) {
  if (!plan) return null

  const base = plan.amount_cents || 0
  const effectiveInterval: 'month' | 'year' =
    billingInterval || (plan.interval === 'year' ? 'year' : 'month')

  const planPct = getPlanDiscountPercent(plan, effectiveInterval)
  const afterPlan =
    planPct > 0 ? Math.max(0, Math.floor((base * (100 - planPct)) / 100)) : base

  let final = afterPlan
  const c = coupon
  if (c?.ok) {
    if (c.type === 'percent' && typeof c.percent_off === 'number') {
      final = Math.max(
        0,
        Math.floor((afterPlan * (100 - Math.floor(c.percent_off))) / 100),
      )
    } else if (c.type === 'fixed' && typeof c.amount_off_cents === 'number') {
      final = Math.max(0, afterPlan - Math.floor(c.amount_off_cents))
    }
  }

  const fmt = (v: number) => {
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: (plan.currency || 'BRL').toUpperCase(),
      }).format(v / 100)
    } catch {
      return `R$ ${(v / 100).toFixed(2)}`
    }
  }

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="mb-2 text-sm font-medium">Resumo</div>
      <div className="space-y-1 text-xs text-foreground/80">
        <div>
          Plano:{' '}
          <span className="text-foreground/90 font-medium">{plan.name}</span>
        </div>
        <div>
          Preço:{' '}
          {final !== base ? (
            <>
              <span className="mr-1 line-through">{fmt(base)}</span>
              <span className="font-medium">{fmt(final)}</span>
            </>
          ) : (
            <span className="font-medium">{fmt(base)}</span>
          )}{' '}
          / {effectiveInterval === 'year' ? 'ano' : 'mes'}
        </div>
        {!!(couponCode || '').trim() && (
          <div>
            Cupom:{' '}
            <span className="font-medium">
              {(couponCode || '').toUpperCase()}
            </span>{' '}
            {c?.ok ? '(aplicado)' : '(invalido)'}
            {c?.ok && (
              <span className="ml-2 inline-flex items-center rounded bg-emerald-600/15 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                Cupom aplicado no Stripe
              </span>
            )}
          </div>
        )}
        {!!(affiliateLabel || '').trim() && (
          <div>
            Afiliado:{' '}
            <span className="font-medium">{affiliateLabel}</span>
          </div>
        )}
      </div>
      {isTrialMode && coupon?.ok && (
        <div className="mt-3 rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-100">
          O desconto do cupom foi aplicado e o valor <span className="font-semibold">{fmt(final)}</span> sera cobrado ao final do Trial Rewards, no momento da ativacao do plano selecionado.
        </div>
      )}
    </div>
  )
}
